import React, { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { formatDate } from '../../utils/helpers';
import EmptyState from '../common/EmptyState';
import DataTable from '../DataTable';
import Button from '../common/Button';
import { t as tr } from '../../i18n';

const ProjectTimeline = ({ embedded = false, parkId = '' }) => {
  const {
    currentUser,
    renewablePortfolio,
    getProjectTimeline,
    addProjectTimelineTask,
    preferences,
    updatePreferences
  } = useAppContext();

  const preferredParkId = parkId || preferences?.projectContext?.parkId || 'all';
  const defaultProjectId = useMemo(() => {
    if (!renewablePortfolio || renewablePortfolio.length === 0) return '';
    if (preferredParkId !== 'all') {
      const match = renewablePortfolio.find((project) => project.parkId === preferredParkId);
      if (match) return match.id;
    }
    return renewablePortfolio[0]?.id || '';
  }, [renewablePortfolio, preferredParkId]);

  const [selectedProjectId, setSelectedProjectId] = useState(defaultProjectId);
  const [ownerFilter, setOwnerFilter] = useState('Todos');
  const [partidaFilter, setPartidaFilter] = useState('Todas');
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState('');
  const [taskForm, setTaskForm] = useState({
    partida: '',
    subPartida: '',
    name: '',
    start: '',
    end: '',
    owner: '',
    progress: 0
  });

  const canEdit = ['project_manager', 'super_user'].includes(currentUser?.role || '');

  const selectedProject = useMemo(
    () => renewablePortfolio.find((project) => project.id === selectedProjectId) || null,
    [renewablePortfolio, selectedProjectId]
  );

  useEffect(() => {
    if (!defaultProjectId) return;
    setSelectedProjectId(defaultProjectId);
  }, [defaultProjectId]);

  useEffect(() => {
    const parkId = selectedProject?.parkId || null;
    if (!parkId) return;
    updatePreferences({ projectContext: { parkId } });
  }, [selectedProject?.parkId, updatePreferences]);

  if (!renewablePortfolio || renewablePortfolio.length === 0) {
    return (
      <EmptyState
        title={tr('Cronograma: no hay proyectos de Desarrollo')}
        description={tr(
          'El cronograma se gestiona por proyecto. Primero debes crear un proyecto (borrador o completo).'
        )}
        checklist={[
          'Crear proyecto en Desarrollo > Portafolio (o en Administración).',
          'Asignar Project Manager (PM).',
          'Luego agregar tareas del Gantt.'
        ]}
        primaryAction={{ label: 'Ir a Desarrollo', pageId: 'development-portfolio' }}
        secondaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
      />
    );
  }

  const timelineTasks = useMemo(() => {
    if (!selectedProject?.parkId) return [];
    const timeline = getProjectTimeline(selectedProject.parkId);
    return timeline?.tasks || [];
  }, [selectedProject, getProjectTimeline]);

  const owners = useMemo(() => {
    const set = new Set(timelineTasks.map((task) => task.owner).filter(Boolean));
    return ['Todos', ...Array.from(set)];
  }, [timelineTasks]);

  const partidas = useMemo(() => {
    const set = new Set(timelineTasks.map((task) => task.partida).filter(Boolean));
    return ['Todas', ...Array.from(set)];
  }, [timelineTasks]);

  const filteredTasks = useMemo(
    () =>
      timelineTasks.filter((task) => {
        const ownerOk = ownerFilter === 'Todos' || task.owner === ownerFilter;
        const partidaOk = partidaFilter === 'Todas' || task.partida === partidaFilter;
        return ownerOk && partidaOk;
      }),
    [timelineTasks, ownerFilter, partidaFilter]
  );

  const delayed = filteredTasks.filter((task) => new Date(task.end) < new Date() && Number(task.progress) < 100).length;

  const tableData = useMemo(
    () =>
      filteredTasks.map((task) => ({
        ...task,
        startLabel: formatDate(task.start),
        endLabel: formatDate(task.end)
      })),
    [filteredTasks]
  );

  const columns = useMemo(
    () => [
      { key: 'partida', label: tr('Partida'), sortable: true, render: (value) => value || 'General' },
      { key: 'subPartida', label: tr('Sub partida'), sortable: true, render: (value, row) => value || row.name },
      { key: 'progress', label: tr('% avance'), align: 'right', sortable: true, render: (value) => `${value}%` },
      { key: 'startLabel', label: tr('Fecha inicio'), sortable: true },
      { key: 'endLabel', label: tr('Fecha termino'), sortable: true },
      { key: 'owner', label: tr('Responsable'), sortable: true, render: (value) => value || 'Sin asignar' }
    ],
    []
  );

  useEffect(() => {
    setPartidaFilter('Todas');
    setOwnerFilter('Todos');
    setCreateOpen(false);
    setCreateError('');
    setTaskForm({
      partida: '',
      subPartida: '',
      name: '',
      start: '',
      end: '',
      owner: '',
      progress: 0
    });
  }, [selectedProjectId]);

  const handleCreateTask = (event) => {
    event.preventDefault();
    if (!selectedProject?.parkId) {
      setCreateError(tr('Proyecto inválido para cronograma (parkId no disponible).'));
      return;
    }
    if (!taskForm.start || !taskForm.end) {
      setCreateError(tr('Debes indicar fecha de inicio y término.'));
      return;
    }
    const startDate = new Date(`${taskForm.start}T00:00:00`);
    const endDate = new Date(`${taskForm.end}T00:00:00`);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      setCreateError(tr('Fechas inválidas.'));
      return;
    }
    if (endDate < startDate) {
      setCreateError(tr('La fecha de término no puede ser anterior al inicio.'));
      return;
    }
    const progress = Math.min(100, Math.max(0, Number(taskForm.progress || 0)));
    const name = taskForm.name || taskForm.subPartida || 'Nueva tarea';
    const created = addProjectTimelineTask(selectedProject.parkId, {
      name,
      partida: taskForm.partida || 'General',
      subPartida: taskForm.subPartida || '',
      start: taskForm.start,
      end: taskForm.end,
      owner: taskForm.owner || '',
      progress
    });
    if (!created) {
      setCreateError(tr('No fue posible crear la tarea. Revisa fechas y campos obligatorios.'));
      return;
    }
    setCreateError('');
    setCreateOpen(false);
    setTaskForm({
      partida: taskForm.partida,
      subPartida: '',
      name: '',
      start: '',
      end: '',
      owner: taskForm.owner,
      progress: 0
    });
  };

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{tr('Cronograma')}</h1>
            <p className="text-sm text-slate-500">{tr('Planificacion, dependencias y seguimiento de hitos por proyecto.')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={selectedProjectId}
              onChange={(event) => setSelectedProjectId(event.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-xl text-sm"
            >
              {renewablePortfolio.map((project) => (
                <option key={project.id} value={project.id}>
                  {tr(project.name)}
                </option>
              ))}
            </select>
            <select
              value={ownerFilter}
              onChange={(event) => setOwnerFilter(event.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-xl text-sm"
            >
              {owners.map((owner) => (
                <option key={owner}>{tr(owner)}</option>
              ))}
            </select>
            <select
              value={partidaFilter}
              onChange={(event) => setPartidaFilter(event.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-xl text-sm"
            >
              {partidas.map((partida) => (
                <option key={partida}>{tr(partida)}</option>
              ))}
            </select>
            {canEdit && (
              <Button variant="primary" onClick={() => setCreateOpen(true)}>{tr('Agregar tarea')}</Button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-xs text-slate-500">{tr('Tareas')}</p><p className="text-3xl font-bold text-slate-900">{tr(filteredTasks.length)}</p></div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"><p className="text-xs text-emerald-700">{tr('Completadas')}</p><p className="text-3xl font-bold text-emerald-800">{tr(filteredTasks.filter((t) => Number(t.progress) >= 100).length)}</p></div>
        <div className="rounded-xl border border-danger-100 bg-danger-50 p-4"><p className="text-xs text-danger-700">{tr('Atrasadas')}</p><p className="text-3xl font-bold text-danger-700">{tr(delayed)}</p></div>
      </div>

      {createOpen && canEdit && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="font-semibold text-emerald-900">{tr('Nueva tarea')}</p>
              <p className="text-xs text-emerald-800">{tr('Crea una tarea para el cronograma del proyecto seleccionado.')}</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setCreateOpen(false);
                setCreateError('');
              }}
            >{tr('Cerrar')}</Button>
          </div>

          <form onSubmit={handleCreateTask} className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              value={taskForm.partida}
              onChange={(event) => setTaskForm((prev) => ({ ...prev, partida: event.target.value }))}
              placeholder={tr('Partida (ej: Permisos / Ingeniería / Obras)')}
              className="px-3 py-2 rounded-xl border border-emerald-200 bg-white text-sm"
            />
            <input
              value={taskForm.subPartida}
              onChange={(event) => setTaskForm((prev) => ({ ...prev, subPartida: event.target.value }))}
              placeholder={tr('Sub-partida (opcional)')}
              className="px-3 py-2 rounded-xl border border-emerald-200 bg-white text-sm"
            />
            <input
              value={taskForm.name}
              onChange={(event) => setTaskForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder={tr('Nombre tarea (si vacío usa sub-partida)')}
              className="px-3 py-2 rounded-xl border border-emerald-200 bg-white text-sm"
            />
            <div className="grid grid-cols-2 gap-3 md:col-span-2">
              <div>
                <label className="block text-xs font-semibold text-emerald-900 mb-1">{tr('Inicio')}</label>
                <input
                  type="date"
                  value={taskForm.start}
                  onChange={(event) => setTaskForm((prev) => ({ ...prev, start: event.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-emerald-200 bg-white text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-emerald-900 mb-1">{tr('Término')}</label>
                <input
                  type="date"
                  value={taskForm.end}
                  onChange={(event) => setTaskForm((prev) => ({ ...prev, end: event.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-emerald-200 bg-white text-sm"
                  required
                />
              </div>
            </div>
            <input
              value={taskForm.owner}
              onChange={(event) => setTaskForm((prev) => ({ ...prev, owner: event.target.value }))}
              placeholder={tr('Responsable (nombre o rol)')}
              className="px-3 py-2 rounded-xl border border-emerald-200 bg-white text-sm"
            />
            <div className="flex items-center gap-3 md:col-span-3">
              <label className="text-xs font-semibold text-emerald-900">{tr('Avance %')}</label>
              <input
                type="number"
                min={0}
                max={100}
                value={taskForm.progress}
                onChange={(event) => setTaskForm((prev) => ({ ...prev, progress: event.target.value }))}
                className="w-28 px-3 py-2 rounded-xl border border-emerald-200 bg-white text-sm"
              />
              <Button type="submit" variant="primary" className="ml-auto">{tr('Guardar tarea')}</Button>
            </div>
            {createError && (
              <div className="md:col-span-3 text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-xl px-3 py-2">
                {tr(createError)}
              </div>
            )}
          </form>
        </div>
      )}

      <DataTable
        data={tableData}
        columns={columns}
        searchPlaceholder={tr('Buscar tareas del cronograma...')}
        emptyTitle={tr('No hay tareas registradas')}
        emptyDescription={tr('No hay hitos registrados para este proyecto con los filtros actuales.')}
      />
    </div>
  );
};

export default ProjectTimeline;
